import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "../client";
import type { CreateItemInput, Item, ItemStatus, UpdateItemInput } from "../types";

const ITEMS_KEY = "items";

interface ItemSearchParams {
  barcode?: string;
  recordId?: string;
  status?: ItemStatus;
  limit?: number;
}

/**
 * Search items.
 */
export function useItemSearch(params: ItemSearchParams) {
  const queryParams = new URLSearchParams();

  if (params.barcode) queryParams.set("barcode", params.barcode);
  if (params.recordId) queryParams.set("recordId", params.recordId);
  if (params.status) queryParams.set("status", params.status);
  if (params.limit) queryParams.set("limit", String(params.limit));

  const queryString = queryParams.toString();

  return useQuery({
    queryKey: [ITEMS_KEY, "search", params],
    queryFn: async () => {
      const url = queryString ? `/catalog/items?${queryString}` : "/catalog/items";
      return get<Item[]>(url);
    },
    enabled: !!(params.barcode || params.recordId || params.status),
  });
}

/**
 * Get a single item by ID or barcode.
 */
export function useItem(id: string | undefined) {
  return useQuery({
    queryKey: [ITEMS_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error("ID is required");
      return get<Item>(`/catalog/items/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Get items for a record.
 */
export function useRecordItems(recordId: string | undefined) {
  return useQuery({
    queryKey: [ITEMS_KEY, "record", recordId],
    queryFn: async () => {
      if (!recordId) throw new Error("Record ID is required");
      return get<Item[]>(`/catalog/items?recordId=${recordId}`);
    },
    enabled: !!recordId,
  });
}

/**
 * Create a new item.
 */
export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateItemInput) => {
      return post<Item>("/catalog/items", input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ITEMS_KEY] });
      // The first copy validates a draft record server-side: refresh its status and counts
      void queryClient.invalidateQueries({ queryKey: ["catalog-records"] });
    },
  });
}

/**
 * Update an existing item.
 */
export function useUpdateItem(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateItemInput) => {
      return patch<Item>(`/catalog/items/${id}`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ITEMS_KEY] });
    },
  });
}

/**
 * Delete an item.
 */
export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/catalog/items/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ITEMS_KEY] });
    },
  });
}
